import { useState, ComponentProps, useRef, useEffect } from 'react';
import { BoxV2 as Box } from 'portal-commons';

interface MoreButtonProps {
  options: Array<{
    label: string;
    value: string;
  }>;
  onSelect: (value: string) => void;
  disabled?: boolean;
  containerSx?: ComponentProps<typeof Box>['sx'];
  menuMinWidth?: string;
}

const MoreButton = ({
  options,
  onSelect,
  disabled = false,
  containerSx,
  menuMinWidth,
}: MoreButtonProps) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLUListElement>(null);

  const handleClick = () => {
    if (!disabled) {
      setOpen(!open);
    }
  };

  const handleSelect = (value: string) => {
    onSelect(value);
    setOpen(false);
    menuRef.current?.blur();
  };

  useEffect(() => {
    if (open) {
      menuRef.current?.focus();
    }
  }, [open]);

  return (
    <Box sx={{ position: 'absolute', ...containerSx }}>
      <Box
        as="span"
        role="button"
        onClick={handleClick}
        sx={{
          cursor: disabled ? 'not-allowed' : 'pointer',
          position: 'relative',
        }}
        data-testid="moreButton"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="62"
          height="40"
          viewBox="0 0 62 40"
          fill="none"
        >
          <rect
            width="62"
            height="40"
            rx="20"
            fill={disabled ? 'rgba(0, 0, 0, 0.12)' : '#D41C54'}
          />
          <circle cx="38.5" cy="20.5" r="2.5" fill="white" />
          <circle cx="30.5" cy="20.5" r="2.5" fill="white" />
          <circle cx="22.5" cy="20.5" r="2.5" fill="white" />
        </svg>
      </Box>
      {open && (
        <Box
          as="ol"
          sx={{
            position: 'absolute',
            top: '100%',
            right: 0,
            margin: 0,
            listStyle: 'none',
            borderRadius: '4px',
            backgroundColor: 'white',
            boxShadow: '0 1px 10.4px -1px rgba(0, 0, 0, 0.25)',
            padding: '5px 0',
            minWidth: menuMinWidth,
            zIndex: 1,
            outline: 'none',
          }}
          tabIndex={0}
          ref={menuRef}
          onBlur={() => setOpen(false)}
          data-testid=""
        >
          {options.map((option) => (
            <Box
              as="li"
              key={option.value}
              onClick={() => handleSelect(option.value)}
              sx={{
                padding: '8px 16px',
                fontSize: 'H200',
                fontWeight: 'normal',
                color: 't.black80',
                cursor: 'pointer',
                transition: 'font-weight 0.2s',
                '&:hover': {
                  fontWeight: 'semiBold',
                },
              }}
            >
              {option.label}
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MoreButton;
